import apiFetch from './apiFetch';

function buildRequestError(defaultMessage, status, data) {
  const error = new Error(data?.error || defaultMessage);
  error.status = status;
  error.data = data;
  return error;
}

async function parseResponseData(response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  return text ? { error: text } : null;
}

export async function fetchUsers({ search = '', role = '', active = true } = {}) {
  const params = new URLSearchParams();

  if (search?.trim()) {
    params.set('search', search.trim());
  }

  if (role?.trim()) {
    params.set('role', role.trim());
  }

  if (active !== null && active !== undefined) {
    params.set('active', String(active));
  }

  const response = await apiFetch(`/api/users${params.toString() ? `?${params.toString()}` : ''}`);
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to load users.', response.status, data);
  }

  return Array.isArray(data) ? data : [];
}

export async function fetchAssignableUsers({ search = '' } = {}) {
  const params = new URLSearchParams({ assignable: 'true' });

  if (search?.trim()) {
    params.set('search', search.trim());
  }

  const response = await apiFetch(`/api/users?${params.toString()}`);
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to load assignable users.', response.status, data);
  }

  return Array.isArray(data) ? data : [];
}

export async function fetchUserById(userId) {
  const response = await apiFetch(`/api/users/${userId}`);
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to load the user.', response.status, data);
  }

  return data;
}

export async function createUser(payload) {
  const response = await apiFetch('/api/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to create the user.', response.status, data);
  }

  return data;
}

export async function updateUser(userId, payload) {
  const response = await apiFetch(`/api/users/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to update the user.', response.status, data);
  }

  return data;
}

export async function resetUserPassword(userId, newPassword) {
  const response = await apiFetch(`/api/users/${userId}/password`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newPassword }),
  });

  if (!response.ok) {
    const data = await parseResponseData(response);
    throw buildRequestError('Failed to reset the password.', response.status, data);
  }
}

export async function fetchTenantOptions() {
  const response = await apiFetch('/api/tenants/options');
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to load tenant options.', response.status, data);
  }

  return Array.isArray(data) ? data : [];
}
